import React from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import get from 'lodash/get';
import first from 'lodash/first';
import isUndefined from 'lodash/isUndefined';
import moment from 'moment-timezone';
import MUIDataTable from 'mui-datatables';
import {
  Button,
  FormControl,
  Grid,
  TextField,
  Tooltip,
  IconButton,
} from '@mui/material';
import { KeyboardBackspace, Map } from '@mui/icons-material';
import Picture from '@mui/icons-material/PhotoCamera';

import {
  OpsBrandNames,
  rideMinutesForDisplay,
  longDateTimeFormat,
  timeZoneHint,
} from '../../lib/utils';
import LightBox from '../common/lightbox';
import WrappedSpinner from '../common/wrapped-spinner';
import withPersistentSettings from '../common/table-with-persistent-settings';
import vehicleActions from '../../actions/vehicles';
import Modal from '../common/modal';
import { RouteMap } from './map/route-map';
import { getMarketVehicleClickHandler } from '../../lib/routes/routeHelper';

class VehicleTripsTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isMapOpen: false,
      isPhotoModalOpen: false,
      mappableRoute: [],
    };
  }

  componentDidMount() {
    const { vehicles: vehiclesBucket, match, fetchVehicleTripsById } = this.props;
    const vehicleId = get(match, 'params.vehicleId');
    if (isUndefined(vehiclesBucket.byId) || isUndefined(vehiclesBucket.byId[vehicleId])) {
      fetchVehicleTripsById(vehicleId);
    }
  }

  get columns() {
    const {
      selectedFilters,
      primarySort,
      match,
      vehicles,
      addToColumnData,
      markets = [],
    } = this.props;
    const { vehicleId } = match.params;
    const vehicleBucket = vehicles;
    const vehicleRides = get(vehicleBucket, `byId.${vehicleId}`, []);
    const isZagster = get(first(vehicleRides), 'vehicle.brand') === OpsBrandNames.ZAGSTER;
    const vehicleMarketId = get(first(vehicleRides), 'vehicle.marketId');
    const market = markets.find((mkt) => mkt._id === vehicleMarketId);
    const marketTz = get(market, 'tz', 'America/New_York');

    const columnData = [
      { name: 'Trip Id' },
      {
        name: 'Start Time',
        options: {
          customBodyRender: (startTime) => (
            moment.tz(startTime, marketTz).format(longDateTimeFormat)
          ),
          hint: timeZoneHint(marketTz),
        },
      },
      {
        name: 'End Time',
        options: {
          customBodyRender: (endTime) => {
            if (endTime) {
              return moment.tz(endTime, marketTz).format(longDateTimeFormat);
            } else {
              return '-';
            }
          },
          hint: timeZoneHint(marketTz),
        },
      },
      { name: 'Duration (min)' },
      { name: 'Distance (km)' },
      {
        name: 'User Email',
        options: {
          display: isZagster,
        },
      },
      {
        name: 'Map',
        options: {
          filter: false,
          sort: false,
          customBodyRender: (data) => {
            if (data && data.length) {
              return (
                <Tooltip title="Click to see trip route on map">
                  <IconButton
                    onClick={() => this.setState({
                      isMapOpen: true,
                      mappableRoute: data,
                    })}
                  >
                    <Map />
                  </IconButton>
                </Tooltip>
              );
            } else {
              return '';
            }
          },
        },
      },
      {
        name: 'Photo',
        options: {
          filter: false,
          customBodyRender: (data) => {
            if (data && data.length) {
              return (
                <Tooltip title="Click to see end of ride photo">
                  <IconButton
                    onClick={() => {
                      this.setState({
                        isPhotoModalOpen: true,
                        imageUrl: data,
                      });
                    }}
                  >
                    <Picture />
                  </IconButton>
                </Tooltip>
              );
            } else {
              return '';
            }
          },
        },
      },
    ];
    return addToColumnData(columnData, selectedFilters, primarySort);
  }

  get data() {
    const { vehicleId } = this.props.match.params;
    const vehicleBucket = this.props.vehicles;
    const vehicleRides = get(vehicleBucket, `byId.${vehicleId}`, []);

    if (isUndefined(vehicleRides)) return [];
    return vehicleRides.map((trip) => [
      trip._id,
      trip.start,
      trip.end,
      trip.end ? rideMinutesForDisplay(trip.durationSeconds) : '–',
      (trip.distanceMeters / 1000).toFixed(2),
      get(trip, 'userEmail', ''),
      get(trip, 'route.features', []),
      get(trip, 'photoAfterRideUrl'),
    ]);
  }

  render() {
    const {
      isMapOpen, mappableRoute, isPhotoModalOpen, imageUrl,
    } = this.state;
    const {
      tableOptions, match, vehicles, history,
    } = this.props;
    const marketId = get(match, 'params.marketId');
    const vehicleId = get(match, 'params.vehicleId');
    const vehicleBucket = vehicles;
    const { isLoading } = vehicleBucket;

    const vehicleRides = get(vehicleBucket, `byId.${vehicleId}`, []);
    const firstVehicleRide = first(vehicleRides);
    const marketName = get(firstVehicleRide, 'market.name', '');
    const vehicleHumanId = get(firstVehicleRide, 'vehicle.humanId', '');

    return (
      <WrappedSpinner isLoading={isLoading}>
        <Grid container>
          <Grid item xs={3}>
            <Button variant="contained" color="primary" onClick={getMarketVehicleClickHandler(history, marketId)}>
              <KeyboardBackspace />
              Back
            </Button>
          </Grid>
          <Grid item xs={9} />

          <Grid item xs={3} />
          <Grid item xs={3}>
            <FormControl fullWidth>
              <TextField
                id="vehicleHumanId"
                label="Vehicle Id"
                value={vehicleHumanId}
                InputProps={{ readOnly: true }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth>
              <TextField
                id="marketName"
                label="Market Name"
                value={marketName}
                InputProps={{ readOnly: true }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <MUIDataTable
              title="Vehicle Trip Info"
              columns={this.columns}
              data={this.data}
              options={tableOptions}
            />
          </Grid>
        </Grid>

        {isPhotoModalOpen && (
          <LightBox
            imageUrl={imageUrl}
            onClose={() => this.setState((state) => ({
              isPhotoModalOpen: !state.isPhotoModalOpen,
            }))}
          />
        )}
        {isMapOpen && (
          <Modal
            fullWidth
            isOpen={isMapOpen}
            onClose={() => this.setState((state) => ({ isMapOpen: !state.isMapOpen }))}
          >
            <RouteMap route={mappableRoute} />
          </Modal>
        )}
      </WrappedSpinner>
    );
  }
}

const mapDispatchToProps = (dispatch) => bindActionCreators({
  ...vehicleActions,
}, dispatch);

const mapStateToProps = ({ vehicles, markets }) => ({
  markets: markets.markets,
  vehicles,
});

export default withPersistentSettings(
  connect(mapStateToProps, mapDispatchToProps)(withRouter(VehicleTripsTable)),
  'VehicleTripsTable',
);
